import { RouteStop } from "@/reducers/routes2gSlice"

/*
 * Which stops of an analyze-order plan go back to gps, and which of them make
 * up the new order.
 *
 * The plan is built on top of the stops the driver's route had when the
 * analysis started (the "base" stops). The dispatcher can take minutes before
 * saving, and meanwhile the live route moves on: the driver leaves a stop and
 * gps retires it, a TMS sync adds one. Measured against the live route alone,
 * a stop the driver has since completed looks exactly like one this order
 * adds — both are missing from it — so it was tagged into the new delivery
 * order and re-created as an open stop (wmtransport, 20.10.2026).
 */

export const stopIds = (stops: RouteStop[]): Set<string> =>
  new Set(stops.flatMap((stop) => (stop.id ? [stop.id] : [])))

// A route_handle encodes the leg from the previous stop, so it is stale once a
// different stop comes before this one; without it gps routes the leg anew.
const withoutRouteHandle = (stop: RouteStop): RouteStop =>
  stop.route_handle ? { ...stop, route_handle: undefined } : stop

const isGoneFromRoute = (
  stop: RouteStop,
  baseStopIds: ReadonlySet<string>,
  liveStopIds: ReadonlySet<string> | undefined,
): boolean =>
  liveStopIds !== undefined &&
  !!stop.id &&
  baseStopIds.has(stop.id) &&
  !liveStopIds.has(stop.id)

/**
 * The planned stops without the base stops that have left the driver's live
 * route since the analysis started (the driver completed them, someone else
 * removed them). Apply it before every re-plan too: gps gives a stop it no
 * longer has a fresh id, and after that it can no longer be told apart from a
 * stop of the new order.
 *
 * With the live route unknown nothing is removed.
 */
export const withoutStopsGoneFromRoute = (
  stops: RouteStop[],
  baseStopIds: ReadonlySet<string>,
  liveStops: RouteStop[] | undefined,
): RouteStop[] => {
  const liveStopIds = liveStops ? stopIds(liveStops) : undefined
  if (!stops.some((stop) => isGoneFromRoute(stop, baseStopIds, liveStopIds))) {
    return stops
  }

  const kept: RouteStop[] = []
  let previousGone = false
  for (const stop of stops) {
    if (isGoneFromRoute(stop, baseStopIds, liveStopIds)) {
      previousGone = true
      continue
    }
    kept.push(previousGone ? withoutRouteHandle(stop) : stop)
    previousGone = false
  }
  return kept
}

/**
 * Puts back the live stops the plan has never seen — added to the route after
 * the analysis started (TMS sync, another dispatcher) — so saving the plan does
 * not delete them. Each goes right after the stop it follows on the live
 * route, or first when nothing it follows is left.
 */
const withStopsAddedMeanwhile = (
  stops: RouteStop[],
  baseStopIds: ReadonlySet<string>,
  liveStops: RouteStop[],
): { stops: RouteStop[]; addedMeanwhile: RouteStop[] } => {
  const plannedIds = stopIds(stops)
  const addedMeanwhile = liveStops.filter(
    (stop) =>
      !!stop.id && !baseStopIds.has(stop.id) && !plannedIds.has(stop.id),
  )
  if (addedMeanwhile.length === 0) return { stops, addedMeanwhile }

  const merged = [...stops]
  for (const stop of addedMeanwhile) {
    const liveIndex = liveStops.indexOf(stop)
    let insertAt = 0
    for (let i = liveIndex - 1; i >= 0; i--) {
      const anchorId = liveStops[i].id
      const anchorIndex = anchorId
        ? merged.findIndex((candidate) => candidate.id === anchorId)
        : -1
      if (anchorIndex >= 0) {
        insertAt = anchorIndex + 1
        break
      }
    }
    const keepsItsPredecessor =
      liveStops[liveIndex - 1]?.id === merged[insertAt - 1]?.id
    merged.splice(
      insertAt,
      0,
      keepsItsPredecessor ? stop : withoutRouteHandle(stop),
    )
    const next = merged[insertAt + 1]
    if (next) merged[insertAt + 1] = withoutRouteHandle(next)
  }
  return { stops: merged, addedMeanwhile }
}

export type OrderRouteUpsert = {
  /** What to send to gps, in the dispatcher's order. */
  stops: RouteStop[]
  /** Stops of the new order, tagged with its delivery order timestamp. */
  orderStops: RouteStop[]
  /** Base stops that left the live route meanwhile — not sent back. */
  goneStops: RouteStop[]
  /** Live stops added after the analysis started — kept on the route. */
  addedMeanwhile: RouteStop[]
}

/**
 * Turns the planned route into the stops to save.
 *
 * - A stop already on the live route is kept as it is.
 * - A base stop no longer on the live route is dropped (and must not come
 *   back as a new stop).
 * - Every other planned stop was added by this flow — the order's own stops,
 *   or one the dispatcher added on the map — and is tagged with
 *   `deliveryOrderTimestamp`, which is what gps groups into the order.
 * - A live stop added after the analysis started is kept in place.
 *
 * @param plannedStops - The route as planned, in the dispatcher's order
 * @param baseStopIds - Ids of the stops on the route when the analysis started
 * @param liveStops - The driver's route now; `undefined` when unknown, in which
 *   case nothing is dropped or added
 * @param deliveryOrderTimestamp - Timestamp identifying the new order
 */
export const buildOrderRouteUpsert = ({
  plannedStops,
  baseStopIds,
  liveStops,
  deliveryOrderTimestamp,
}: {
  plannedStops: RouteStop[]
  baseStopIds: ReadonlySet<string>
  liveStops: RouteStop[] | undefined
  deliveryOrderTimestamp: string
}): OrderRouteUpsert => {
  const liveStopIds = liveStops ? stopIds(liveStops) : undefined
  const isOrderStop = (stop: RouteStop) =>
    !stop.id || (!baseStopIds.has(stop.id) && !liveStopIds?.has(stop.id))

  const goneStops = plannedStops.filter((stop) =>
    isGoneFromRoute(stop, baseStopIds, liveStopIds),
  )
  const planned = withoutStopsGoneFromRoute(
    plannedStops,
    baseStopIds,
    liveStops,
  ).map((stop) =>
    isOrderStop(stop) ? { ...stop, deliveryOrderTimestamp } : stop,
  )
  const orderStops = planned.filter(isOrderStop)

  if (!liveStops) {
    return { stops: planned, orderStops, goneStops, addedMeanwhile: [] }
  }
  const { stops, addedMeanwhile } = withStopsAddedMeanwhile(
    planned,
    baseStopIds,
    liveStops,
  )
  return { stops, orderStops, goneStops, addedMeanwhile }
}
